import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { COVER_LETTER_TEMPLATES, CoverLetterTemplateSVG } from '../data/coverLetterTemplates';
import Header from './Header';
import './TemplatePage.css';
import './CoverLetterTemplatePage.css';

function Toast({ anchorRef, visible }) {
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (visible && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({
        top:  rect.top + window.scrollY,
        left: rect.left + rect.width / 2,
      });
    }
  }, [visible, anchorRef]);

  if (!visible || !pos) return null;

  return ReactDOM.createPortal(
    <div style={{
      position: 'absolute',
      top: pos.top,
      left: pos.left,
      transform: 'translate(-50%, calc(-100% - 8px))',
      width: 'max-content',
      background: '#1a1a1a',
      color: 'white',
      fontSize: 12,
      fontWeight: 600,
      fontFamily: 'var(--font-body)',
      padding: '8px 14px',
      borderRadius: 8,
      zIndex: 9999,
      boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
      pointerEvents: 'none',
    }}>
      You're already using this template
      <div style={{
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid #1a1a1a',
      }}/>
    </div>,
    document.body
  );
}

export default function CoverLetterTemplatePage({ onSelectTemplate, onBack, onHome, resumeTemplateId, fullAppMode = false, user, disabledTemplateId, lastSaved, isSaving, saveFailed }) {
  const [toastId, setToastId] = useState(null);
  const activeRef = useRef(null);
  const matchId = COVER_LETTER_TEMPLATES.find(t => t.resumeTemplateId === resumeTemplateId)?.id ?? null;

  const handleCardClick = (t, el) => {
    if (t.id === disabledTemplateId) {
      activeRef.current = el;
      setToastId(t.id);
      setTimeout(() => setToastId(null), 2000);
      return;
    }
    onSelectTemplate(t);
  };

  return (
    <div className="template-page">
      <Header onHome={onHome ?? onBack} user={user} lastSaved={lastSaved} isSaving={isSaving} saveFailed={saveFailed} />

      <main className="template-page-main">
        <button className="template-page-back" onClick={onBack}>
          {fullAppMode ? '← Back to Resume Template' : '← Back'}
        </button>

        <div className="template-page-hero">
          <div className="template-compare-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            {fullAppMode ? 'Resume + Cover Letter — Step 2 of 2' : 'Cover Letter'}
          </div>
          <h1 className="template-page-title">Choose a cover letter style</h1>
          <p className="template-page-subtitle">
            {matchId
              ? 'The highlighted style matches your resume. Pick any style you like.'
              : 'Each style matches a resume template — pick the one that pairs with your resume.'}
          </p>
        </div>

        <Toast anchorRef={activeRef} visible={!!toastId} />

        <div className="template-page-grid">
          {COVER_LETTER_TEMPLATES.map(t => {
            const isMatch   = t.id === matchId;
            const isCurrent = t.id === disabledTemplateId;
            return (
              <button
                key={t.id}
                className={`template-page-card ${isMatch ? 'cl-template-match' : ''} ${isCurrent ? 'template-page-card-current' : ''}`}
                onClick={(e) => handleCardClick(t, e.currentTarget)}
              >
                <div className="template-preview-clip">
                  <CoverLetterTemplateSVG template={t} />
                  {isMatch && (
                    <div className="cl-match-badge">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      Matches your resume
                    </div>
                  )}
                </div>
                <div className="template-page-info">
                  <span className="template-page-name">{t.name}</span>
                  <span className="template-page-desc">{t.description}</span>
                </div>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
